import { useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Upload, Loader2, CheckCircle2, AlertCircle, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function SolutionAnalyzerPage() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [subject, setSubject] = useState("matematik");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Sadece JPEG, PNG veya WEBP yükle");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Görsel çok büyük (max 6MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      setPreview(data);
      setBase64(data.split(",")[1]);
    };
    reader.readAsDataURL(f);
  };

  const analyze = async () => {
    if (!base64) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/ai/solution/analyze", { image_base64: base64, subject_hint: subject });
      setResult(r.data);
    } catch (e) {
      toast.error("Analiz başarısız. Tekrar dene.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null); setBase64(null); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-5 ela-fade-in" data-testid="solution-analyzer">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Soru Çözüm Analizi</h1>
        <p className="text-sm text-slate-500 mt-1">Çözüm kâğıdını fotoğrafla, AI hatanı bulsun.</p>
      </div>

      <Card className="p-6 rounded-2xl border-slate-200">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              data-testid="upload-dropzone"
              className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-[#FF6B6B] hover:bg-rose-50/20 transition"
            >
              {preview ? (
                <img src={preview} alt="Önizleme" className="max-h-64 mx-auto rounded-xl" />
              ) : (
                <>
                  <div className="w-12 h-12 rounded-2xl bg-rose-50 text-[#FF6B6B] flex items-center justify-center mx-auto mb-3">
                    <Camera className="w-6 h-6" />
                  </div>
                  <p className="font-medium text-slate-700">Fotoğrafı buraya bırak</p>
                  <p className="text-xs text-slate-500 mt-1">veya tıklayıp seç (JPEG, PNG, WEBP · max 6MB)</p>
                </>
              )}
              <input
                ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(e) => onFile(e.target.files?.[0])} className="hidden"
                data-testid="upload-file-input"
              />
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger className="w-36 rounded-xl" data-testid="subject-select">
                  <SelectValue placeholder="Ders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="matematik">Matematik</SelectItem>
                  <SelectItem value="turkce">Türkçe</SelectItem>
                  <SelectItem value="fen">Fen Bilimleri</SelectItem>
                  <SelectItem value="inkilap">İnkılap Tarihi</SelectItem>
                  <SelectItem value="din">Din Kültürü</SelectItem>
                  <SelectItem value="ingilizce">İngilizce</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={analyze} disabled={!base64 || loading}
                data-testid="analyze-btn"
                className="bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl flex-1">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />İnceleniyor…</> :
                  <><Sparkles className="w-4 h-4 mr-2" />İncele</>}
              </Button>
              {preview && (
                <Button variant="outline" onClick={reset} data-testid="reset-btn" className="rounded-xl">
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <div>
            {!result && !loading && (
              <div className="h-full flex flex-col items-center justify-center text-center text-sm text-slate-500 p-6 rounded-2xl bg-slate-50">
                <Upload className="w-8 h-8 text-slate-400 mb-2" />
                <p>Çözüm fotoğrafını yükle ve AI hatalı adımı bulsun.</p>
              </div>
            )}
            {loading && (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 rounded-2xl bg-slate-50">
                <Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin mb-3" />
                <p className="text-sm text-slate-600">Gemini 3 Pro çözümünü inceliyor…</p>
              </div>
            )}
            {result && <AnalysisResult r={result} />}
          </div>
        </div>
      </Card>
    </div>
  );
}

function AnalysisResult({ r }) {
  const ok = r.dogru_mu;
  return (
    <div className="space-y-3" data-testid="analysis-result">
      <div className={`p-4 rounded-2xl border flex items-start gap-3 ${
        ok ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
      }`}>
        {ok ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" /> :
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />}
        <div className="text-sm">
          <div className="font-semibold text-slate-900">{ok ? "Çözüm doğru görünüyor!" : "Bir adımda hata var"}</div>
          <div className="text-slate-600 mt-1">{r.motivasyon}</div>
        </div>
      </div>

      {r.soru_metni && (
        <Section title="Soru">{r.soru_metni}</Section>
      )}
      {r.ogrenci_cozumu_ozet && (
        <Section title="Senin Çözümün">{r.ogrenci_cozumu_ozet}</Section>
      )}
      {!ok && r.hatali_adim && (
        <Section title="Hatalı Adım" tone="warn">{r.hatali_adim}</Section>
      )}
      {r.dogru_cozum && (
        <Section title="Doğru Çözüm" tone="ok">{r.dogru_cozum}</Section>
      )}
      {r.alternatif_yontem && (
        <Section title="Alternatif Yöntem">{r.alternatif_yontem}</Section>
      )}
    </div>
  );
}

function Section({ title, children, tone }) {
  const toneCls = tone === "ok" ? "border-green-100 bg-green-50/30" :
                   tone === "warn" ? "border-amber-100 bg-amber-50/30" : "border-slate-200 bg-white";
  return (
    <div className={`p-4 rounded-xl border ${toneCls}`}>
      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">{title}</div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap">{children}</div>
    </div>
  );
}
